import React, { useState, useEffect, useCallback } from 'react';
import PauseService from '../../../../views/Pause/service';
import UserService from '../../../../views/User/service';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import FlexSelect from 'components/Inputs/FlexSelect';
import FlexTable from 'components/FlexTable';
import Pagination from 'components/Pagination';
import Loader from 'react-loader-spinner';
import { dateRules } from './Assets/rulesValidation';
import { disabledDownloadButtonCsvAndPdf } from '../../utils/disabledDownloadButtonCsvAndPdf';
import { definedMessageGeneratedInPdfCsvDownloads } from '../../utils/definedMessageGeneratePdfCsv';
import DatePicker from '../../../../components/Inputs/DatePicker.js';
import csvImage from '../../../../assets/img/csv.png';
import xlsxImage from '../../../../assets/img/xlsx.png';
import * as fileSaver from 'file-saver';
import { Tooltip } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { UserFlexSelect } from '../../../../components/UserFlexSelect';
import { DatesHelper } from '../../../../utils/classes/DatesHelper';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  CardHeader,
  Button,
  CardBody
} from 'reactstrap';

import {
  ImageButtonContainer,
  ImageButton,
  ImageSize
} from '../../../../assets/styles/image_style';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { useMemorizeFilters } from 'hooks/useMemorizeInputsFilters';
import { POSSIBLE_FILTERS_ENTITIES } from 'hooks/useMemorizeInputsFilters';
import { ClearFiltersButton } from 'components/ClearFiltersButton';
import ServiceCRUDChatTS from 'services/ServiceCRUDCHATTS';

/**
 * @typedef {Object} PauseReportOutput
 * @property {string} dateIn - Date when pause started
 * @property {string} dateOut - Date when pause ended
 * @property {string} durationInPause - Total duration in pause
 * @property {string} agentName - Name of the agent
 * @property {string} pauseName - Name of the pause type
 */

const ServiceChatTS = new ServiceCRUDChatTS('/reports/pause-chat');

const PauseReport = () => {
  const { control, handleSubmit, errors, setValue, getValues } = useForm();
  const [reportData, setReportData] = useState([]);
  const [dataToSubmitDownloads, setDataToSubmitDownloads] = useState();
  const [pauses, setPauses] = useState([]);
  const [usersShowDisable, setUsersShowDisable] = useState(true);
  const [agents, setAgents] = useState([]);
  const [loadingForm, setLoadingForm] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [quantity, setQuantity] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const limitGenerationPDF = parseInt(
    process.env.REACT_APP_LIMIT_GENERATION_PDF || 25000
  );
  const [messagemGenerateCsv, setMessagemGenerateCsv] = useState();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [media, setMedia] = useState('');

  const { getMemorizedFilters, memorizeFilters, clearMemorizedFilters } =
    useMemorizeFilters(POSSIBLE_FILTERS_ENTITIES.REPORT_CHAT_PAUSES);

  const tableColumns = [
    {
      dataField: 'dateIn',
      text: 'Data Início',
      sort: true
    },
    {
      dataField: 'dateOut',
      text: 'Data Fim',
      sort: true
    },
    {
      dataField: 'durationInPause',
      text: 'Duração',
      sort: true
    },
    {
      dataField: 'agentName',
      text: 'Agente',
      sort: true
    },
    {
      dataField: 'pauseName',
      text: 'Tipo de Pausa',
      sort: true
    }
  ];

  useEffect(() => {
    PauseService.getAll()
      .then((response) => {
        setPauses([...response.data.data]);
      })
      .catch((err) => {
        console.log('err: ' + err);
      });
  }, []);

  useEffect(() => {
    getUser(true);
  }, [usersShowDisable]);

  const getUser = (showDisable = false) => {
    if (!showDisable) {
      setLoadingForm(true);
    }
    UserService.getAll(false, undefined, undefined, usersShowDisable)
      .then((response) => {
        setAgents([...response.data.data]);
        if (!showDisable) {
          setLoadingForm(false);
        }
      })
      .catch((err) => {
        console.log('err: ' + err);
        if (!showDisable) {
          setLoadingForm(false);
        }
      });
  };

  useEffect(() => {
    const message = definedMessageGeneratedInPdfCsvDownloads(
      quantity,
      limitGenerationPDF
    );
    setMessagemGenerateCsv(message.csvMessage);
  }, [quantity, limitGenerationPDF]);

  const downloadMedia = useCallback(
    async (typeReport, type, fileName) => {
      try {
        setLoadingDownload(true);

        const fileType = fileName.includes('.xlsx') ? 'xlsx' : 'csv';
        setMedia(fileType);

        const downloadUrl = ServiceChatTS.getDownloadsPauseReportNewWindow(
          dataToSubmitDownloads,
          fileType
        );

        window.open(downloadUrl, '_blank');

        toast.success(
          'Download iniciado! O arquivo será baixado em uma nova janela.',
          {
            autoClose: 4000,
            closeOnClick: true
          }
        );
      } catch (err) {
        toast.error(err?.message || 'Ops, ocorreu um erro!', {
          autoClose: 3000,
          closeOnClick: true
        });
      } finally {
        setLoadingDownload(false);
        setMedia('');
      }
    },
    [dataToSubmitDownloads]
  );

  const toISOFormat = useCallback(async (dateTimeString) => {
    const [date] = dateTimeString.split(' ');
    return DatesHelper.convertToISOFormat(date);
  }, []);

  const getDataDTO = useCallback(
    async (data) => {
      return {
        startDate: `${await toISOFormat(data.startDate)}`,
        endDate: `${await toISOFormat(data.endDate)}`,
        pauseId: data.pauseId,
        agentLogin: data.agentLogin,
        page: data.page || activePage, // Use passed page or active page
        size: 10
      };
    },
    [activePage, toISOFormat]
  );

  const getReports = useCallback(
    (selectedPage = 1, dataToSend) => {
      const newActivePage = selectedPage || activePage;
      setLoadingTable(true);

      ServiceChatTS.getAllReportsType(
        'analytical',
        selectedPage,
        dataToSend,
        10
      )
        .then((res) => {
          const { rows, count } = res.data.data; // Updated to match response structure
          setReportData(rows);
          setQuantity(count);
          setActivePage(newActivePage);
          if (rows.length === 0) {
            toast.info('Nenhum registro foi encontrado!');
          }
          setLoadingTable(false);
        })
        .catch((err) => {
          setLoadingTable(false);
          toast.error('Erro ao buscar relatórios');
          console.error('erro:', err);
        });
    },
    [activePage]
  );

  useEffect(() => {
    // Load memorized filters on component mount
    const memorizedFilters = getMemorizedFilters();
    if (memorizedFilters) {
      setValue(
        'startDate',
        memorizedFilters.startDate || DatesHelper.getTodayInBrazilianFormat()
      );
      setValue(
        'endDate',
        memorizedFilters.endDate || DatesHelper.getTodayInBrazilianFormat()
      );
      setValue('pauseId', memorizedFilters.pauseId || []);
      setValue('agentLogin', memorizedFilters.agentLogin || []);

      // If there were memorized filters, trigger search
      onSubmit(memorizedFilters, false);
    }
  }, []);

  const onSubmit = useCallback(
    async (data, isNotFromUseEffect = true) => {
      try {
        memorizeFilters({
          ...data,
          page: activePage
        });

        const pageToUse = isNotFromUseEffect ? 1 : activePage;
        setActivePage(pageToUse);

        const dataToSend = await getDataDTO({
          ...data,
          endDate: DatesHelper.compileDateAndTime(data.endDate, '23:59:59'),
          page: pageToUse
        });

        getReports(pageToUse, dataToSend);
        setDataToSubmitDownloads(dataToSend);
      } catch (err) {
        toast.error(err || 'Erro desconhecido por favor, informar o suporte!', {
          autoClose: 3000,
          closeOnClick: true
        });
      }
    },
    [getDataDTO, getReports, activePage, memorizeFilters]
  );

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const handlePageChange = useCallback(
    (pageNumber) => {
      const dataToSend = {
        ...dataToSubmitDownloads,
        page: pageNumber
      };
      setActivePage(pageNumber);
      getReports(pageNumber, dataToSend);
    },
    [dataToSubmitDownloads, getReports]
  );

  const handleReset = () => {
    // Clear form
    setValue('startDate', DatesHelper.getTodayInBrazilianFormat());
    setValue('endDate', DatesHelper.getTodayInBrazilianFormat());
    setValue('pauseId', []);
    setValue('agentLogin', []);

    // Clear memorized filters
    clearMemorizedFilters();

    // Reset page
    setActivePage(1);

    // Clear results
    setReportData([]);
    setQuantity(0);
  };

  return (
    <>
      <SimpleHeader name="Form validation" parentName="Forms" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>Relatório de Pausas</h3>
                </CardHeader>
                <CardBody>
                  {loadingForm ? (
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <ClearFiltersButton
                        styles={{
                          position: 'absolute',
                          right: '1rem',
                          top: '2rem'
                        }}
                        handleOnClick={handleReset}
                      />

                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Data inicial*
                          </label>
                          <Controller
                            render={(props) => (
                              <DatePicker
                                fieldName="startDate"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="startDate"
                            rules={dateRules}
                            defaultValue={
                              getMemorizedFilters()?.startDate ||
                              new Date().toLocaleDateString('pt-br')
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="startDate"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Data final*
                          </label>
                          <Controller
                            render={(props) => (
                              <DatePicker
                                fieldName="endDate"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="endDate"
                            defaultValue={
                              getMemorizedFilters()?.endDate ||
                              new Date().toLocaleDateString('pt-br')
                            }
                            rules={{
                              ...dateRules,
                              validate: async (value) => {
                                if (
                                  new Date(await toISOFormat(value, true)) <
                                  new Date(
                                    await toISOFormat(
                                      getValues()['startDate'],
                                      true
                                    )
                                  )
                                ) {
                                  return 'A data final não pode ser anterior à inicial!';
                                } else {
                                  return true;
                                }
                              }
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="endDate"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-12">
                        <Col md="6">
                          <label className="form-control-label">Usuário</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={agents}
                                isMulti={true}
                                value={props.value}
                                valueController={setValue}
                                closeMenuOnSelect={false}
                                isClearable={true}
                                fieldName="agentLogin"
                                labelName="name"
                                valueName="login"
                              />
                            )}
                            control={control}
                            name="agentLogin"
                            defaultValue={
                              getMemorizedFilters()?.agentLogin || []
                            }
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Tipo de Pausa
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={pauses}
                                isMulti={true}
                                value={props.value}
                                valueController={setValue}
                                closeMenuOnSelect={false}
                                isClearable={true}
                                fieldName="pauseId"
                                labelName="name"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="pauseId"
                            defaultValue={getMemorizedFilters()?.pauseId || []}
                          />
                        </Col>
                      </Row>

                      <div style={{ marginTop: '10px' }}>
                        <UserFlexSelect
                          usersShowDisable={usersShowDisable}
                          setUsersShowDisable={setUsersShowDisable}
                          name={'Usuário'}
                        />
                      </div>

                      <Row className="mb-4">
                        <Col md="12" style={ImageButtonContainer}>
                          <button
                            style={ImageButton}
                            onClick={() =>
                              downloadMedia(
                                'pausa',
                                'application/csv',
                                'Relatório_Pausas.csv'
                              )
                            }
                            disabled={
                              loadingDownload ||
                              disabledDownloadButtonCsvAndPdf(
                                'csv',
                                quantity,
                                limitGenerationPDF
                              ) ||
                              !dataToSubmitDownloads ||
                              reportData.length === 0
                            }
                          >
                            <Tooltip
                              title={
                                <p style={{ fontSize: '14px' }}>
                                  {messagemGenerateCsv}
                                </p>
                              }
                              arrow
                            >
                              <img src={csvImage} alt="csv" style={ImageSize} />
                            </Tooltip>
                          </button>

                          {/* Botão XLSX */}
                          <button
                            style={ImageButton}
                            onClick={() =>
                              downloadMedia(
                                'pausa',
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                'Relatório_Pausas.xlsx'
                              )
                            }
                            disabled={
                              loadingDownload ||
                              disabledDownloadButtonCsvAndPdf(
                                'xlsx',
                                quantity,
                                limitGenerationPDF
                              ) ||
                              !dataToSubmitDownloads ||
                              reportData.length === 0
                            }
                          >
                            <Tooltip
                              title={
                                <p style={{ fontSize: '14px' }}>
                                  {reportData.length === 0
                                    ? 'Realize uma busca primeiro para baixar o relatório'
                                    : 'Baixar relatório em Excel (XLSX)'}
                                </p>
                              }
                              arrow
                            >
                              {media === 'xlsx' && loadingDownload ? (
                                <Loader
                                  type="ThreeDots"
                                  visible={true}
                                  height="40"
                                  width="40"
                                  color={systemConfiguration.primary_color}
                                />
                              ) : (
                                <img
                                  src={xlsxImage}
                                  alt="xlsx"
                                  style={ImageSize}
                                />
                              )}
                            </Tooltip>
                          </button>
                        </Col>
                      </Row>

                      <div style={{ marginBottom: '1rem' }}>
                        <Button
                          color="primary"
                          type="submit"
                          style={{
                            ...buttonStyle(systemConfiguration.primary_color)
                          }}
                        >
                          Buscar
                        </Button>
                      </div>
                    </Form>
                  )}
                </CardBody>
                {loadingTable ? (
                  <div className="py-4 table-responsive">
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  </div>
                ) : (
                  <>
                    {dataToSubmitDownloads && reportData.length > 0 && (
                      <div style={{ paddingInline: '1.5rem' }}>
                        <FlexTable
                          keyField="id"
                          data={reportData}
                          columns={tableColumns}
                          sortConfig={sortConfig}
                          setSortConfig={setSortConfig}
                        />
                      </div>
                    )}
                    {dataToSubmitDownloads && reportData.length > 0 && (
                      <Pagination
                        activePage={activePage}
                        totalItemsCount={quantity}
                        onChange={handlePageChange}
                      />
                    )}
                  </>
                )}
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default PauseReport;
